import api from './api';

// Interface para os contadores básicos do dashboard
export interface DashboardContadores {
  success: boolean;
  contadores: {
    usuarios: {
      total: number;
      ativos: number;
      inativos: number;
    };
    tarefas: {
      total: number;
      pendentes: number;
      emAndamento: number;
      concluidas: number;
    };
    departamentos: {
      total: number;
    };
    taxaConclusao: number;
  };
}

// Interface para tarefas por status
export interface TarefasPorStatus {
  success: boolean;
  tarefasPorStatus: Record<string, number>;
}

// Interface para tarefas por prioridade
export interface TarefasPorPrioridade {
  success: boolean;
  tarefasPorPrioridade: Record<string, number>;
}

// Interface para tarefas por departamento (antiga - manter compatibilidade)
export interface TarefasPorDepartamento {
  success: boolean;
  tarefasPorDepartamento: Record<string, number>;
}

// Interface para departamentos com detalhes de tarefas (NOVA)
export interface DepartamentosComDetalhes {
  success: boolean;
  departamentos: Array<{
    id: string;
    nome: string;
    descricao?: string;
    ultimaAtualizacao?: string;
    tarefas: {
      totalAtivas: number;
      pendentes: number;
      aIniciar: number;
      emAndamento: number;
      aguardando: number;
      ajustes: number;
      concluidas: number;
      concluidasComAtraso: number;
      emAndamentoComAtraso: number;
      emAndamentoNoPrazo?: number;
      arquivadas: number;
      ativasNaoArquivadas: number;
    };
  }>;
}

// Interface para usuários mais ativos
export interface UsuariosMaisAtivos {
  success: boolean;
  usuariosMaisAtivos: Array<{
    id: string;
    nome: string;
    tarefasAtivas: number;
  }>;
}

// Interface para estatísticas completas do dashboard
export interface EstatisticasCompletas {
  success: boolean;
  estatisticas: {
    usuarios: {
      total: number;
      ativos: number;
      inativos: number;
    };
    tarefas: {
      ativas: {
        total: number;
        pendentes: number;
        emAndamento: number;
        ajustes: number;
        concluidas: number;
      };
      arquivadas: {
        total: number;
        concluidas: number;
        ajustes: number;
      };
      geral: {
        total: number;
        taxaConclusao: number;
        taxaArquivamento: number;
      };
    };
    departamentos: {
      total: number;
    };
    distribuicao: {
      porStatus: Record<string, number>;
      porPrioridade: Record<string, number>;
      porDepartamento: Record<string, number>;
    };
    temporal: {
      porDiaSemana: Array<{
        diaSemana: string;
        diaSemanaNum: number;
        totalTarefas: number;
      }>;
      diaMaisOcupado: {
        diaSemana: string;
        totalTarefas: number;
        percentual: number;
      };
      diaMenosOcupado: {
        diaSemana: string;
        totalTarefas: number;
        percentual: number;
      };
    };
    usuariosMaisAtivos: Array<{
      id: string;
      nome: string;
      tarefasAtivas: number;
    }>;
  };
}

// Interface para dados do dashboard do usuário
export interface DashboardUsuarioResponse {
  success: boolean;
  estatisticasUsuario: {
    tarefasAtivas: number;
    tarefasConcluidas: number;
  };
  tarefasPorStatus: Record<string, number>;
  tarefasRecebidasPorStatus: Record<string, number>;
  tarefasEnviadasPorStatus: Record<string, number>;
  tarefasPorPrioridade: Record<string, number>;
  tarefasRecentesPorDia: Array<{
    data: string;
    quantidade: number;
  }>;
}

export type DashboardUsuarioData = Omit<DashboardUsuarioResponse, 'success'>;

// Obter contadores básicos do dashboard administrativo
export const obterContadoresDashboard = async (): Promise<DashboardContadores> => {
  try {
    const response = await api.get('/dashboard/contadores');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter contadores do dashboard:', error);
    throw error;
  }
};

// Obter tarefas por status
export const obterTarefasPorStatus = async (): Promise<TarefasPorStatus> => {
  try {
    const response = await api.get('/dashboard/tarefas-status');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter tarefas por status:', error);
    throw error;
  }
};

// Obter tarefas por prioridade
export const obterTarefasPorPrioridade = async (): Promise<TarefasPorPrioridade> => {
  try {
    const response = await api.get('/dashboard/tarefas-prioridade');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter tarefas por prioridade:', error);
    throw error;
  }
};

// Obter tarefas por departamento
export const obterTarefasPorDepartamento = async (): Promise<TarefasPorDepartamento> => {
  try {
    const response = await api.get('/dashboard/tarefas-departamento');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter tarefas por departamento:', error);
    throw error;
  }
};

// Obter usuários mais ativos
export const obterUsuariosMaisAtivos = async (): Promise<UsuariosMaisAtivos> => {
  try {
    const response = await api.get('/dashboard/usuarios-ativos');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter usuários mais ativos:', error);
    throw error;
  }
};

// Obter todas as estatísticas do dashboard em uma única chamada
export const obterEstatisticasCompletas = async (): Promise<EstatisticasCompletas> => {
  try {
    const response = await api.get('/dashboard/estatisticas-completas');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter estatísticas completas do dashboard:', error);
    throw error;
  }
};

// Manter compatibilidade com código existente
export const obterDadosDashboard = async (): Promise<DashboardUsuarioData> => {
  try {
    const response = await api.get('/estatisticas');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter dados do dashboard do usuário:', error);
    throw error;
  }
};

export const obterAtividadesRecentes = async (limite: number = 5) => {
  try {
    const response = await api.get(`/atividades?limite=${limite}`);
    return response.data.atividades;
  } catch (error) {
    console.error('Erro ao obter atividades recentes:', error);
    throw error;
  }
};

export const obterEstatisticasDepartamento = async (departamentoId: string) => {
  try {
    const response = await api.get(`/departamentos/${departamentoId}`);
    return response.data.departamento.estatisticas;
  } catch (error) {
    console.error('Erro ao obter estatísticas do departamento:', error);
    throw error;
  }
};

// Interface para métricas temporais
export interface MetricasTemporais {
  success: boolean;
  metricasTemporais: {
    tarefasPorDiaSemana: Array<{
      diaSemana: string;
      diaSemanaNum: number;
      totalTarefas: number;
    }>;
    tarefasPorHora: Array<{
      hora: number;
      totalTarefas: number;
    }>;
    tempoMedioConclusaoPorMes: Array<{
      mes: string;
      totalTarefas: number;
      mediaDiasConclusao: number;
    }>;
    tarefasPorTrimestre: Array<{
      ano: string;
      trimestre: number;
      totalTarefas: number;
      concluidas: number;
      ajustes: number;
    }>;
    resumo: {
      diaMaisOcupado: {
        diaSemana: string;
        totalTarefas: number;
        percentual: number;
      };
      diaMenosOcupado: {
        diaSemana: string;
        totalTarefas: number;
        percentual: number;
      };
      horaMaisOcupada: {
        hora: number;
        totalTarefas: number;
      };
    };
  };
}

// Interface para tarefas arquivadas
export interface TarefasArquivadas {
  success: boolean;
  tarefasArquivadas: {
    total: number;
    porStatus: Record<string, number>;
    ultimas: Array<{
      id: string;
      titulo: string;
      status: string;
      prioridade: string;
      dataCriacao: string;
      dataConclusao: string;
    }>;
  };
}

// Interface para análise de desempenho
export interface AnaliseDesempenho {
  success: boolean;
  analiseDesempenho: {
    tempoPorDepartamento: Array<{
      departamento: string;
      totalTarefas: number;
      tarefasConcluidas: number;
      mediaHorasConclusao: number;
    }>;
    usuariosDesempenho: Array<{
      id: string;
      nome: string;
      totalTarefas: number;
      mediaHorasConclusao: number;
    }>;
    distribuicaoMensal: Array<{
      mes: string;
      total: number;
      concluidas: number;
      ajustes: number;
      emAndamento: number;
      pendentes: number;
    }>;
    tarefasMaisDemoradas: Array<{
      id: string;
      titulo: string;
      status: string;
      horasAteConclusao: number;
    }>;
    resumo: {
      mediaGeralHorasConclusao: number;
      departamentoMaisRapido: {
        departamento: string;
        totalTarefas: number;
        tarefasConcluidas: number;
        mediaHorasConclusao: number;
      } | null;
      usuarioMaisRapido: {
        id: string;
        nome: string;
        totalTarefas: number;
        mediaHorasConclusao: number;
      } | null;
    };
  };
}

// Interface para verificação de inconsistências
export interface VerificacaoInconsistencias {
  success: boolean;
  verificacaoInconsistencias: {
    inconsistencias: {
      tarefasSemResponsavel: number;
      tarefasSemDepartamento: number;
      concluidasSemData: number;
      usuariosSemDepartamento: number;
      tarefasStatusInvalido: number;
    };
    temInconsistencias: boolean;
    resumo: {
      totalInconsistencias: number;
      inconsistenciaCritica: boolean;
    };
  };
}

// Obter métricas temporais
export const obterMetricasTemporais = async (): Promise<MetricasTemporais> => {
  try {
    const response = await api.get('/dashboard/metricas-temporais');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter métricas temporais:', error);
    throw error;
  }
};

// Obter tarefas arquivadas
export const obterTarefasArquivadas = async (): Promise<TarefasArquivadas> => {
  try {
    const response = await api.get('/dashboard/tarefas-arquivadas');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter tarefas arquivadas:', error);
    throw error;
  }
};

// Obter departamentos com detalhes de tarefas (NOVO - OTIMIZADO)
export const obterDepartamentosComDetalhes = async (): Promise<DepartamentosComDetalhes> => {
  try {
    const response = await api.get('/dashboard/tarefas-departamento');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter departamentos com detalhes:', error);
    throw error;
  }
};

// Obter análise de desempenho
export const obterAnaliseDesempenho = async (): Promise<AnaliseDesempenho> => {
  try {
    const response = await api.get('/dashboard/analise-desempenho');
    return response.data;
  } catch (error) {
    console.error('Erro ao obter análise de desempenho:', error);
    throw error;
  }
};

// Verificar inconsistências
export const verificarInconsistencias = async (): Promise<VerificacaoInconsistencias> => {
  try {
    const response = await api.get('/dashboard/verificar-inconsistencias');
    return response.data;
  } catch (error) {
    console.error('Erro ao verificar inconsistências:', error);
    throw error;
  }
};
